import type { UseQueryOptions } from "@tanstack/react-query";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";

import {
  checkAccountVerifiedApi,
  checkEmailVerifiedApi,
  fetchAssignee,
  fetchProfileStats,
  fetchUser,
  fetchUserById,
  fetchUserDetailsApi,
  fetchUserPreferenceApi,
  fetchUserSessionApi,
  fetchWorkspaceDetails,
  getUserlist,
  getAdminUserlist,
  getUserRoles,
  getRolePermissions,
  getUserOrgs,
  getUserProjectRoles,
  fetchDemoRequestsApi,
} from "./users.api";

export const useFetchUserDetails = (options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ["userDetails"],
    queryFn: fetchUserDetailsApi,
    staleTime: 5 * 60 * 1000, // 5 min
    ...options,
  });
};

export const useFetchUser = (userId: number) => {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
  });
};

export function useFetchUserQueries(userIds: number[]) {
  const uniqueIds = Array.from(new Set(userIds));

  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ["user", id],
      queryFn: () => fetchUser(id),
      enabled: !!id,
    })),
  });
}

export const useFetchUserPreference = (options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ["userPreference"],
    queryFn: fetchUserPreferenceApi,
    staleTime: 5 * 60 * 1000, // 5 min
    ...options,
  });
};

export const useFetchUserSession = () => {
  return useQuery({
    queryKey: ["userSession"],
    queryFn: fetchUserSessionApi,
    staleTime: 5 * 60 * 1000, // 5 min
  });
};

export const useFetchWorkspaceDetails = (versionId: string, options = {}) => {
  return useQuery({
    queryKey: ["workspaceDetails", versionId],
    queryFn: () => fetchWorkspaceDetails(versionId),
    enabled: !!versionId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      // Don't retry on 403/401 — user doesn't have access, retrying won't help
      const status = error?.response?.status;
      if (status === 403 || status === 401) return false;
      return failureCount < 3;
    },
    ...options,
  });
};

export const useEmailVerified = (
  email: string,
  options?: Partial<UseQueryOptions<any, Error, any, [string, string]>>,
) => {
  return useQuery({
    queryKey: ["userEmailVerified", email],
    queryFn: () => checkEmailVerifiedApi(email),
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
    enabled: !!email && (options?.enabled ?? true),
    ...options,
  });
};

export const useAccountVerified = (
  email: string,
  options?: Partial<UseQueryOptions<any, Error, any, [string, string]>>,
) => {
  return useQuery({
    queryKey: ["userAccountVerified", email],
    queryFn: () => checkAccountVerifiedApi(email),
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
    enabled: !!email && (options?.enabled ?? true),
    ...options,
  });
};
export const useAssignees = () => {
  return useQuery({
    queryKey: ["assignees"],
    queryFn: fetchAssignee,
  });
};

export const useUserListInfinite = (options?: {
  enabled?: boolean;
  orgId?: number | null;
}) =>
  useInfiniteQuery({
    queryKey: ["userList", options?.orgId ?? null],
    queryFn: ({ pageParam = 0 }) =>
      getUserlist({ page: pageParam as number, size: 10 }),
    getNextPageParam: (lastPage: {
      data: { number: number; totalPages: number };
    }) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
  });

export const useAllUsers = (enabled = true) =>
  useQuery({
    queryKey: ["allUsers"],
    queryFn: () => getUserlist(),
    enabled,
  });

export const useGetUserRoles = (enabled = true) =>
  useQuery({
    queryKey: ["userRoles"],
    queryFn: getUserRoles,
    enabled,
  });

export const useGetRolePermissions = (roleId: number | null | undefined) =>
  useQuery({
    queryKey: ["rolePermissions", roleId],
    queryFn: () => getRolePermissions(roleId!),
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000,
  });

export const useGetUserOrgs = () =>
  useQuery({
    queryKey: ["userOrgs"],
    queryFn: getUserOrgs,
    staleTime: 5 * 60 * 1000,
  });
// export const useGetUserRoles = () => {
//   return useQuery({
//     queryKey: ["userRoles"],
//     queryFn: getUserRoles,
//   });
// };

export const useAdminUserListInfinite = (options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: ["adminUserList"],
    queryFn: ({ pageParam = 0 }) =>
      getAdminUserlist({ page: pageParam as number, size: 25 }),
    getNextPageParam: (lastPage: {
      data: { number: number; totalPages: number };
    }) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
  });

export const useUserById = (id: number) => {
  return useQuery({
    queryKey: ["userById", id],
    queryFn: () => fetchUserById(id),
    enabled: !!id, // avoids calling with undefined
  });
};

export const useFetchDemoRequests = (params?: {
  page?: number;
  size?: number;
}) => {
  return useQuery({
    queryKey: ["demoRequests", params],
    queryFn: () => fetchDemoRequestsApi(params),
  });
};

export type UserProjectRole = {
  projectId: number;
  projectName: string;
  roleId: number | null;
  roleName: string;
};

export const useUserProjectRoles = () =>
  useQuery<UserProjectRole[]>({
    queryKey: ["userProjectRoles"],
    queryFn: async () => {
      const res = await getUserProjectRoles();
      return (res as any)?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useProfileStats = (enabled = true) =>
  useQuery({
    queryKey: ["profileStats"],
    queryFn: fetchProfileStats,
    staleTime: 2 * 60 * 1000,
    enabled,
  });
